import React, { useCallback, useEffect, useState } from 'react';
import { get } from 'lodash';
import { useFieldArray } from 'react-hook-form';
import {
  ATMForm,
  ATMHeader,
  ATMTable,
  ATMField,
  ATMButton,
  ATMGrid,
  ATMInput,
} from 'shared-it-appmod-ui';
import { FileCategory, SettingKey } from 'src/constants';
import Lang from 'src/libraries/language';
import { IRequestFormMethods, IStructureId } from 'src/models/request.model';
import { useSetting } from 'src/hooks/setting.hook';
import Confirm from 'src/components/atoms/confirm/confirm.component';
import FormUpload from 'src/components/organisms/form-upload/form-upload.component';
import LabelRequired from 'src/components/atoms/label-required/label-required.component';

type IProps = IRequestFormMethods & {
  category: string;
};

const FlightRequestConstructionStructure: React.FC<IProps> = ({
  category,
  control,
  errors,
}) => {
  const { fields, append, remove } = useFieldArray<IStructureId>({
    control,
    name: `${category}.structureInfos`,
  });
  const maxfield = useSetting(SettingKey.MaxStructures, 25);
  const [isConfirm, setIsConfirm] = useState<Partial<IStructureId>>();

  const handleAdd = useCallback(() => {
    append({ structureId: undefined });
  }, [append]);

  useEffect(() => {
    if (
      !fields.length &&
      get(control, `defaultValuesRef.current.${category}.structureInfos`, [])
        .length === 0
    ) {
      handleAdd();
    }
  }, [control, fields, category, handleAdd]);

  const handleRemove = useCallback(
    (index) => {
      const data = fields[index];

      if (data && data.structureKey) {
        setIsConfirm(data);
      } else {
        remove(index);
      }
    },
    [fields, remove]
  );

  return (
    <>
      <ATMHeader size="small" className="text-underline">
        {Lang.LBL_STRUCTURE_IDS}
      </ATMHeader>

      <ATMGrid>
        <ATMGrid.Column width={10}>
          <ATMTable>
            <ATMTable.Header>
              <ATMTable.Row>
                <ATMTable.HeaderCell width={14}>
                  <LabelRequired>{Lang.LBL_STRUCTURE_ID}</LabelRequired>
                </ATMTable.HeaderCell>
                <ATMTable.HeaderCell width={2} />
              </ATMTable.Row>
            </ATMTable.Header>

            <ATMTable.Body>
              {fields.map((item, i) => (
                <ATMTable.Row key={item.id}>
                  <ATMTable.Cell>
                    {item.structureKey && (
                      <ATMField
                        key={`structureKey_${category}_${item.id}`}
                        name={`${category}.structureInfos[${i}].structureKey`}
                        defaultValue={item.structureKey}
                        className="hidden"
                        as={ATMInput}
                        control={control}
                        type="hidden"
                      />
                    )}
                    <ATMField
                      key={`structureId_${item.id}`}
                      as={ATMInput}
                      name={`${category}.structureInfos[${i}].structureId`}
                      defaultValue={item.structureId}
                      control={control}
                      placeholder={Lang.LBL_ENTER}
                      error={get(
                        errors,
                        `${category}.structureInfos[${i}].structureId`,
                        false
                      )}
                    />
                  </ATMTable.Cell>
                  <ATMTable.Cell textAlign="right">
                    {fields.length > 1 && (
                      <ATMButton
                        icon="trash alternate outline"
                        type="button"
                        onClick={() => handleRemove(i)}
                      />
                    )}
                  </ATMTable.Cell>
                </ATMTable.Row>
              ))}
            </ATMTable.Body>
            <ATMTable.Footer>
              <ATMTable.Row>
                <ATMTable.HeaderCell colSpan={4}>
                  <ATMButton
                    secondary
                    icon="plus"
                    type="button"
                    disabled={fields.length >= parseInt(maxfield, 10)}
                    onClick={handleAdd}
                    content={Lang.LBL_ADD}
                  />
                </ATMTable.HeaderCell>
              </ATMTable.Row>
            </ATMTable.Footer>
          </ATMTable>

          <Confirm
            open={!!isConfirm}
            size="tiny"
            onCancel={() => setIsConfirm(undefined)}
            header={Lang.TTL_CONFIRM_DELETE}
            content={Lang.formatString(
              Lang.MSG_CONFIRM_DELETE,
              isConfirm?.structureId ?? ''
            )}
            onConfirm={() => {
              if (isConfirm) {
                const index = fields.findIndex(
                  (value) => value.structureKey === isConfirm.structureKey
                );
                remove(index);
                setIsConfirm(undefined);
              }
            }}
          />
        </ATMGrid.Column>
      </ATMGrid>
      <ATMGrid>
        <ATMGrid.Column width={10}>
          <ATMForm.Field>
            <label>{Lang.NTE_ATTACH_FILE_ASSOCIATED_WITH_STRUCTURES}</label>

            <FormUpload
              control={control}
              errors={errors}
              name={`${category}.structureFiles`}
              allowedTypes={[FileCategory.Structure]}
              inline
            />
          </ATMForm.Field>
        </ATMGrid.Column>
      </ATMGrid>
    </>
  );
};

export default FlightRequestConstructionStructure;
